import React, { useRef, useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
import "./Learner.css";
import LearnerRegistration from "../../components/LearnerRegistration/LearnerRegistration";
import { FaSearch } from "react-icons/fa";
import { FaRegEdit } from "react-icons/fa";
import { RiDeleteBinLine } from "react-icons/ri";
import axios from "axios";
import Loader from "../../components/LoaderModal";
import Modal from "../../components/Modal";
import { TbReportAnalytics } from "react-icons/tb";
import * as XLSX from "xlsx";
import { getLearnersApi, importLearners } from "../../api/api";
import { CiImport } from "react-icons/ci";
import Papa from 'papaparse';

const Learner = () => {
    const { themeStyles, theme } = useTheme();

    const [learner, setLearner] = useState([]);
    const [editRowId, setEditRowId] = useState(null); // Track the row being edited
    const [editData, setEditData] = useState({});
    const [isLoading, setIsLoading] = useState(false); // Loading state
    const [registerLearnerView, setRegisterLearnerView] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [activeLearner, setActiveLearner] = useState(null);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [totalPages, setTotalPages] = useState(1); // Total pages for pagination
    const fileInputRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [uploading, setUploading] = useState(false);
    const [limit, setLimit] = useState(10); // default to 10 entries per page
    const isFirstRender = useRef(true);

    const getPaginationRange = (currentPage, totalPages) => {
        const maxVisible = 7;
        const sideCount = 2;

        if (totalPages <= maxVisible) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const range = [];

        if (currentPage <= sideCount + 2) {
            for (let i = 1; i <= sideCount + 3; i++) range.push(i);
            range.push("...");
            range.push(totalPages);
        } else if (currentPage >= totalPages - (sideCount + 1)) {
            range.push(1);
            range.push("...");
            for (let i = totalPages - (sideCount + 2); i <= totalPages; i++) range.push(i);
        } else {
            range.push(1);
            range.push("...");
            for (let i = currentPage - 1; i <= currentPage + 1; i++) range.push(i);
            range.push("...");
            range.push(totalPages);
        }

        return range;
    };

    // Normalize keys: trim and convert to lowercase
    const normalizeData = (data) =>
        data.map((row) => {
            const normalizedRow = {};
            Object.keys(row).forEach((key) => {
                normalizedRow[key.trim().toLowerCase()] = row[key];
            });
            return normalizedRow;
        });

    const handleFileSelect = (event) => {
        console.log("File selected:", event.target.files[0]);
        const file = event.target.files[0];
        if (!file) return;

        const fileExtension = file.name.split('.').pop().toLowerCase();

        setUploading(true);

        if (fileExtension === 'csv') {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: async (results) => {
                    let cleanedData = normalizeData(results.data);
                    cleanedData = cleanedData.map(item => ({
                        ...item,
                        name: item.name?.trim() || "Unknown",
                        phoneNumber: '1231231231'
                    }));

                    // ✅ Add the same check here:
                    const requiredFields = ['name', 'password', 'batch', 'phonenumber', 'email', 'prn'];


                    const hasAllFields = requiredFields.every(field => Object.keys(cleanedData[0] || {}).includes(field));

                    if (!hasAllFields) {
                        alert("Missing required columns. Required: " + requiredFields.join(", "));
                        setUploading(false);
                        return;
                    }
                    await uploadLearners(cleanedData);
                }
            });
        } else if (['xls', 'xlsx'].includes(fileExtension)) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
                let cleanedData = normalizeData(jsonData);

                cleanedData = cleanedData.map(item => ({
                    ...item,
                    name: item.name?.trim() || "Unknown",
                    phoneNumber: '1231231231'
                }));

                // ✅ Add this check here:
                const requiredFields = ['name', 'password', 'batch', 'phonenumber', 'email', 'prn'];
                const hasAllFields = requiredFields.every(field => Object.keys(cleanedData[0] || {}).includes(field));

                if (!hasAllFields) {
                    alert("Missing required columns. Required: " + requiredFields.join(", "));
                    setUploading(false);
                    return;
                }

                await uploadLearners(cleanedData);
            };
            reader.readAsArrayBuffer(file);
        } else {
            alert("Unsupported file format. Please upload CSV or Excel.");
            setUploading(false);
        }

        fileInputRef.current.value = null; // Reset file input
    };

    const uploadLearners = async (learners) => {
        try {
            const res = await importLearners({ learners });
            alert(res.message || "Import successful!");
            await getLearners({ page: 1, limit });
        } catch (err) {
            console.error(err);
            alert("Import failed. Check file format and data.");
        } finally {
            setUploading(false);
        }
    };

    const getLearnerQuizReport = async (learnerId) => {
        try {
            setIsLoading(true);
            // const response = await getLearnerResult(String(learnerId));
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_BASEURL}/result/learner/${String(learnerId)}`,
            );
            await exportToExcel(response.data);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
        }
    }

    // Function to export data to Excel
    const exportToExcel = (data) => {
        // Transform data
        const formattedData = data.map((item) => ({
            "Student Name": item.learnerName || "Unknown", // First column
            "Learner ID": item.learnerId,
            "Total Questions": item.totalQuestions,
            "Correct Answers": item.correctAnswers,
            "Marks": item.marks,
            "Quiz ID": item.quizId,
            "Session ID": item.sessionId,
            "Quiz Number": item.quizNumber || "N/A",
            "Quiz Name": item.quizName || "N/A",
        }));

        // Create a worksheet
        const ws = XLSX.utils.json_to_sheet(formattedData);

        // Create a workbook and append the worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Quiz Results");

        // Write the Excel file
        XLSX.writeFile(wb, "quiz_results.xlsx");
    };

    const getLearners = async (params = {}) => {
        setIsLoading(true);
        try {
            const response = await getLearnersApi(params);
            console.log("Learners fetched successfully:", response);
            setLearner(response.data);
            setIsLoading(false);
            setTotalPages(response.totalPages); // Set total pages for pagination
        } catch (error) {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getLearners({ page: currentPage, limit });
    }, [currentPage, limit]);


    const handleEditClick = (learner) => {
        setEditRowId(learner.id);
        setEditData(learner); // Pre-fill the edit fields
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditData({ ...editData, [name]: value });
    };

    const updateLearner = async (learnerId, payload) => {
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_BASEURL}/learner/learners/${learnerId}`, // Endpoint
                {
                    name: payload.name,
                    password: payload.password,
                    batch: payload.batch,
                    email: payload.email,
                    dob: payload.dob,
                    phoneNumber: payload.phoneNumber,
                }, // Payload
                {
                    headers: {
                        "Content-Type": "application/json", // Set JSON header
                    },
                }
            );

            console.log("Learner updated successfully:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error updating learner:", error.response?.data || error.message);
            throw error; // Re-throw error for further handling
        }
    };

    const handleSaveClick = async () => {
        await updateLearner(editRowId, editData);
        // alert("Learner data updated");
        setLearner(
            learner.map((learner) =>
                learner.id === editRowId ? { ...learner, ...editData } : learner
            )
        );
        setEditRowId(null); // Exit edit mode
    };

    const handleCancelClick = () => {
        setEditRowId(null); // Exit edit mode
    };


    const formatDate = (isoString) => {
        if (!isoString) return "--/--/--"; // Handle empty input
        return new Date(isoString).toISOString().split('T')[0];
    };

    const handleRemoveClick = (learner) => {
        setShowModal(true); // Show the modal
        setActiveLearner(learner.id)
    };

    const handleYes = async () => {
        try {
            const response = await axios.delete(`${process.env.REACT_APP_BACKEND_BASEURL}/learner/learners/${activeLearner}`);
            alert('Learner deleted successfully:', response.data);
        } catch (error) {
            alert('Error deleting learner:', error.message);
        }
        setActiveLearner(null);
        setShowModal(false);
        getLearners();
    };

    const handleCancel = () => {
        setActiveLearner(null);
        setShowModal(false); // Close the modal
    };

    const newLearnerAdded = () => {
        setRegisterLearnerView(false);
        getLearners();
    }

    // Update the debounce value after 1 second of no input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 1000); // 1 second debounce

        return () => {
            clearTimeout(handler); // Clear timeout if the input changes
        };
    }, [search]);

    // Fetch learners whenever debouncedSearch changes
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return; // Skip the initial call
        }
        let isCancelled = false;

        const fetchLearners = async () => {
            if (debouncedSearch.trim() === "") {
                if (!isCancelled) getLearners();
                return;
            }

            // setIsLoading(true);
            try {
                // const response = await getLearners({ search: debouncedSearch });
                if (!isCancelled) getLearners({ search: debouncedSearch });
            } catch (error) {
                console.error("Error fetching learners:", error);
            } finally {
                if (!isCancelled) setIsLoading(false);
            }
        };

        fetchLearners();

        return () => {
            isCancelled = true;
        };
    }, [debouncedSearch]);

    const onSearchTextChange = (e) => {
        setSearch(e.target.value);
    };

    return (
        <div
            className="learner-page w-full flex flex-col overflow-auto no-scrollbar"
            style={{
                // backgroundColor: themeStyles.background,
                color: themeStyles.text,
                padding: 0,
                margin: 0,
            }}
        >
            {!registerLearnerView && <div>
                {(isLoading || uploading) && <Loader />}
                <div className="p-5 px-7">
                    {/* First Row */}
                    <div className="flex flex-wrap md:flex-nowrap items-center gap-3">
                        {/* Left Section */}
                        <div className="flex flex-wrap md:flex-nowrap items-center space-x-3 gap-2">
                            <span className="font-medium" style={{ color: themeStyles.text }}>
                                Show
                            </span>
                            <select
                                className="py-2 px-3 border rounded-md focus:outline-none focus:ring-2"
                                value={limit}
                                onChange={(e) => {
                                    setLimit(parseInt(e.target.value, 10));
                                    setCurrentPage(1); // Reset to first page when limit changes
                                }}
                                style={{
                                    backgroundColor: themeStyles.cardBackground,
                                    color: themeStyles.text,
                                    borderColor: themeStyles.text,
                                }}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>

                            <span className="font-medium" style={{ color: themeStyles.text }}>
                                entries
                            </span>
                        </div>

                        {/* Search Bar */}
                        <div
                            className="flex items-center p-2 rounded-lg border w-full md:w-auto"
                            style={{
                                backgroundColor: themeStyles.cardBackground,
                                borderColor: themeStyles.text,
                            }}
                        >
                            <FaSearch className="text-gray-400 mr-2" />
                            <input
                                type="text"
                                placeholder="Search..."
                                className="w-full focus:outline-none bg-transparent"
                                style={{ color: themeStyles.text }}
                                value={search}
                                onChange={(e) => onSearchTextChange(e)}
                            />
                        </div>

                        {/* Add Learner Button */}
                        {/* Hidden file input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv, .xlsx, .xls"
                            style={{ display: "none" }}
                            onChange={handleFileSelect}
                        />
                        <button
                            onClick={() => {
                                fileInputRef.current.click()
                            }
                            }
                            className="flex items-center ml-auto px-4 py-2 rounded-md transition duration-200 whitespace-nowrap bg-green-500"
                            style={{
                                marginLeft: 'auto'
                            }}
                            disabled={uploading}
                        >
                            <p className="text-white"><CiImport className="text-white" size={20} /></p>
                            <p className="ml-2 text-white">{uploading ? "Importing..." : "Import"}</p>
                        </button>

                        <button
                            onClick={() => setRegisterLearnerView(true)}
                            className="flex items-center px-4 py-2 rounded-md transition duration-200 whitespace-nowrap"
                            style={{
                                backgroundColor: themeStyles.primaryColor,
                                color: themeStyles.cardContentBackground,
                            }}
                        >
                            <p className="text-white">+</p>
                            <p className="ml-2 text-white">Add Learner</p>
                        </button>
                    </div>
                </div>

                <div className="block w-full overflow-x-auto overflow-y-auto">
                    <table className="learner-table min-w-[1200px] border-collapse">
                        <thead>
                            <tr>
                                <th className="px-4 py-2">PRN</th>
                                <th className="px-4 py-2">Name</th>
                                <th className="px-4 py-2">Batch</th>
                                <th className="px-4 py-2">Password</th>
                                <th className="px-4 py-2">Phone Number</th>
                                <th className="px-4 py-2">Email</th>
                                <th className="px-4 py-2">Date Of Birth</th>
                                <th className="px-4 py-2">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!isLoading && learner.length === 0 && (
                                <tr className="border-b">
                                    <td colSpan="8" className="text-center py-4 justify-center align-middle" style={{ color: themeStyles.text }}>
                                        No learners found.  Please add a learner.
                                    </td>
                                </tr>
                            )}
                            {learner.map((learner) =>
                                editRowId === learner.id ? (
                                    <tr key={learner.id} className="border-b">
                                        <td className="px-4 py-2">{learner.prn}</td>
                                        <td className="px-4 py-2">
                                            <input
                                                className="w-full px-2 py-1 border rounded"
                                                type="text"
                                                name="name"
                                                value={editData.name}
                                                onChange={handleChange}
                                                style={{
                                                    backgroundColor: themeStyles.cardBackground,
                                                    borderColor: themeStyles.text,
                                                }}
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input
                                                className="w-full px-2 py-1 border rounded"
                                                type="text"
                                                name="batch"
                                                value={editData.batch}
                                                onChange={handleChange}
                                                style={{
                                                    backgroundColor: themeStyles.cardBackground,
                                                    borderColor: themeStyles.text,
                                                }}
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input
                                                className="w-full px-2 py-1 border rounded"
                                                type="text"
                                                name="password"
                                                value={editData.password}
                                                onChange={handleChange}
                                                style={{
                                                    backgroundColor: themeStyles.cardBackground,
                                                    borderColor: themeStyles.text,
                                                }}
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input
                                                className="w-full px-2 py-1 border rounded"
                                                type="text"
                                                name="phoneNumber"
                                                value={editData.phoneNumber}
                                                onChange={handleChange}
                                                style={{
                                                    backgroundColor: themeStyles.cardBackground,
                                                    borderColor: themeStyles.text,
                                                }}
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input
                                                className="w-full px-2 py-1 border rounded"
                                                type="text"
                                                name="email"
                                                value={editData.email}
                                                onChange={handleChange}
                                                style={{
                                                    backgroundColor: themeStyles.cardBackground,
                                                    borderColor: themeStyles.text,
                                                }}
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input
                                                className="w-full px-2 py-1 border rounded"
                                                type="date"
                                                name="dob"
                                                value={formatDate(editData.dob)}
                                                onChange={handleChange}
                                                style={{
                                                    backgroundColor: themeStyles.cardBackground,
                                                    borderColor: themeStyles.text,
                                                }}
                                            />
                                        </td>
                                        <td className="px-4 py-2 flex justify-between items-center">
                                            <button className="px-3 py-1 text-white bg-blue-500 rounded" onClick={handleSaveClick}>
                                                Save
                                            </button>
                                            <button
                                                className="text-sm text-white bg-red-500 rounded-full w-6 h-6 flex items-center justify-center ml-2"
                                                onClick={handleCancelClick}
                                                aria-label="Close"
                                            >
                                                ×
                                            </button>
                                        </td>

                                    </tr>
                                ) : (
                                    <tr key={learner.id} className="border-b">
                                        <td className="px-4 py-2 text-center">{learner.prn}</td>
                                        <td className="px-4 py-2">{learner.name}</td>
                                        <td className="px-4 py-2">{learner.batch}</td>
                                        <td className="px-4 py-2">{"*******"}</td>
                                        <td className="px-4 py-2">{learner.phoneNumber}</td>
                                        <td className="px-4 py-2">{learner.email}</td>
                                        <td className="px-4 py-2">{formatDate(learner.dob)}</td>
                                        <td className="px-4 py-2 flex items-center space-x-2">
                                            <button onClick={() => handleEditClick(learner)}>
                                                <FaRegEdit size={20} className="text-blue-500" />
                                            </button>
                                            <button onClick={() => handleRemoveClick(learner)}>
                                                <RiDeleteBinLine size={20} className="text-red-600" />
                                            </button>
                                            <button onClick={() => getLearnerQuizReport(learner.id)}>
                                                <TbReportAnalytics size={20} className="text-green-500" />
                                            </button>
                                        </td>
                                    </tr>
                                )
                            )}
                        </tbody>
                    </table>
                </div>


                {!isLoading && learner.length !== 0 && (
                    <div className="flex items-center m-10 justify-center space-x-1 mt-10 flex-wrap">
                        {/* Previous Button */}
                        <button
                            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                            className={`px-3 py-2 text-sm font-medium rounded ${currentPage === 1 ? 'cursor-not-allowed opacity-50' : 'hover:underline'}`}
                            disabled={currentPage === 1}
                            style={{ color: theme === "light" ? themeStyles.primaryColor : '#FFF' }}
                        >
                            Previous
                        </button>

                        {/* Page Numbers */}
                        {totalPages > 0 && getPaginationRange(currentPage, totalPages).map((page, idx) => {
                            if (page === '...') {
                                return (
                                    <span key={idx} className="px-3 py-2 text-sm font-medium text-gray-500">
                                        ...
                                    </span>
                                );
                            }

                            return (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-2 text-sm font-medium rounded ${currentPage === page ? 'font-bold' : ''}`}
                                    style={{
                                        backgroundColor: currentPage === page ? themeStyles.primaryColor : "transparent",
                                        color: currentPage === page ? "#FFF" : (theme === "light" ? themeStyles.primaryColor : "#FFF"),
                                        border: `1px solid ${themeStyles.primaryColor}`,
                                    }}
                                >
                                    {page}
                                </button>
                            );
                        })}

                        {/* Next Button */}
                        <button
                            onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                            className={`px-3 py-2 text-sm font-medium rounded ${currentPage === totalPages ? 'cursor-not-allowed opacity-50' : 'hover:underline'}`}
                            disabled={currentPage === totalPages}
                            style={{ color: theme === "light" ? themeStyles.primaryColor : '#FFF' }}
                        >
                            Next
                        </button>
                    </div>
                )}


            </div>}

            {registerLearnerView && <div className="px-14 py-8">
                <LearnerRegistration registrationView={() => setRegisterLearnerView(false)} learnerAdded={() => newLearnerAdded} />
            </div>}

            {showModal && (
                <Modal
                    title="Confirm Deletion"
                    message="Are you sure you want to delete this learner?"
                    onYes={() => handleYes}
                    onCancel={handleCancel}
                />
            )}
        </div>
    );
};

export default Learner;
